import { useThrottleFn } from '@react-cmpt/use-throttle'
import React, { ChangeEvent, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import './styles.css'
import { EXPORT_SIZE, useContextMotifGenerator } from '../../../contexts/motifGeneratorContext'
import { useContextSelectedMotifs } from '../../../contexts/selectedMotifContext'
import { PositionXY } from '../../../types/PositionXY'
import { getPagePositionXY } from '../../../utils/getPagePositionXY'
import CheckboxSwitch from '../../CheckboxSwitch'
import { ExportLayoutModalResponse } from '../ExportLayoutModal'
import MaskSquare from './masks/MaskSquare'
import { toJpeg } from 'html-to-image'
import { useContextOnboarding } from '../../../contexts/onboardingContext'
import { FeatureDiscoveryElement } from '../../../types/FeatureDiscoveryElement'
import { t } from 'i18next'
import { cn } from '../../../utils/cn'

const DEFAULT_IMAGE_SIZE = 150
const MIN_IMAGE_SIZE = DEFAULT_IMAGE_SIZE / 4
const MAX_IMAGE_SIZE = DEFAULT_IMAGE_SIZE * 4
const SINGLE_EXPORT_SIZE = 3000

// Assets from the public folder, referenced by 'styles.css'.
// A root-relative url() inside the stylesheet would be resolved (and rejected) by webpack.
const assetVariables = {
  '--mask-square': "url('/images/mask-square.svg')",
  '--rotate-icon': "url('/icons/circle-arrow-icon.svg')",
} as React.CSSProperties

export type DraggableImage = {
  id: string
  originalSVGUrl: string | null
  vectorizedUrl: string | null
  position: PositionXY
  size: number
  selected: boolean
  rotation: number
}

interface GeneratorProps {
  exportResponse?: ExportLayoutModalResponse
  onExportComplete?: () => void
}

const FreeLayoutGenerator = ({ exportResponse, onExportComplete }: GeneratorProps) => {
  const { featureDiscoveryElementsOpen, setFeatureDiscoveryElementsOpen } = useContextOnboarding()
  const {
    freeGeneratorTouchEventPageXY,
    setFreeGeneratorTouchEventPageXY,
    selectedMotifForFreeGeneration,
    setSelectedMotifForFreeGeneration,
    freeLayoutGeneratorState,
    setFreeLayoutGeneratorState,
  } = useContextMotifGenerator()
  const { selectedMotifs } = useContextSelectedMotifs()
  const [images, setImages] = useState<DraggableImage[]>(
    freeLayoutGeneratorState?.images ? freeLayoutGeneratorState?.images : [],
  )
  const rotating = useRef<boolean>(false)
  const resizing = useRef<boolean>(false)
  const lastPos = useRef<PositionXY>({ x: 0, y: 0 })
  const dragStartPosXY = useRef<PositionXY | undefined>(undefined)
  const resizeDirection = useRef<number | undefined>(undefined)
  const containerRef = useRef<HTMLDivElement>(null)
  const canvasToExportRef = useRef<HTMLCanvasElement>(null)
  const clipLayerRef = useRef<HTMLDivElement>(null)
  const visibleLayerRef = useRef<HTMLDivElement>(null)
  const exportLayerRef = useRef<HTMLDivElement>(null)
  const [imageAreaLoop, setImageAreaLoop] = useState(1)
  const { setExportInvoked, handleExport } = useContextMotifGenerator()
  const [defaultImageSize, setDefaultImageSize] = useState(DEFAULT_IMAGE_SIZE)
  const [minImageSize, setMinImageSize] = useState(MIN_IMAGE_SIZE)
  const [maxImageSize, setMaxImageSize] = useState(MAX_IMAGE_SIZE)
  const motifRemoverRef = useRef<HTMLDivElement>(null)
  const motifRemoverClientRect = useRef<DOMRect | undefined>(undefined)
  const [isDragging, setIsDragging] = useState(false)
  const removeEnabled = useRef(false)
  const ratio = useRef(1)
  const selectedImageRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const rat = (Math.min(window.innerWidth, window.innerHeight) / EXPORT_SIZE) * 0.75
    ratio.current = rat
    setDefaultImageSize(DEFAULT_IMAGE_SIZE * (1 + 1 - rat) * 2)
    setMinImageSize(MIN_IMAGE_SIZE * (1 + 1 - rat))
    setMaxImageSize(MAX_IMAGE_SIZE * (1 + 1 - rat))

    if (freeLayoutGeneratorState) {
      setImageAreaLoop(freeLayoutGeneratorState.isMirrored ? 2 : 1)
    }
  }, [])

  useEffect(() => {
    if (!selectedMotifForFreeGeneration) return
    if (
      !images
        .map((image: DraggableImage) => image.id)
        .includes(`${selectedMotifForFreeGeneration.id}-${images.length}`)
    ) {
      setImages([
        // deselect all the images
        ...images.map(image => {
          image.selected = false
          return image
        }),
        // add the recently grabbed image
        {
          id: `${selectedMotifForFreeGeneration.id}-${images.length}`,
          position: {
            x: freeGeneratorTouchEventPageXY
              ? freeGeneratorTouchEventPageXY.x * (1 / ratio.current) - defaultImageSize / 2
              : 0,
            y: freeGeneratorTouchEventPageXY
              ? freeGeneratorTouchEventPageXY.y * (1 / ratio.current) - defaultImageSize / 2
              : window.innerHeight / 2,
          },
          originalSVGUrl: selectedMotifForFreeGeneration.originalSVGUrl,
          vectorizedUrl: selectedMotifForFreeGeneration.vectorizedUrl,
          size: defaultImageSize,
          selected: true,
          rotation: 0,
        },
      ])
      setIsDragging(true)
    }
  }, [selectedMotifForFreeGeneration])

  useEffect(() => {
    if (!freeGeneratorTouchEventPageXY || !selectedMotifForFreeGeneration) return
    const selectedImage = getSelectedImage()
    if (!selectedImage) return
    setIsDragging(true)
    moveImage(selectedImage, freeGeneratorTouchEventPageXY)
  }, [freeGeneratorTouchEventPageXY])

  useEffect(() => {
    if (!selectedMotifs) return

    setImages(
      images.filter(image => {
        const imageUrl = image.originalSVGUrl || image.vectorizedUrl
        return selectedMotifs.some(
          motif => (motif.originalSVGUrl || motif.vectorizedUrl) === imageUrl,
        )
      }),
    )
  }, [selectedMotifs])

  useEffect(() => {
    if (images.length > 0) {
      setFeatureDiscoveryElementsOpen([
        ...featureDiscoveryElementsOpen.filter(
          elem => elem !== FeatureDiscoveryElement.FREE_LAYOUT,
        ),
      ])
      setFreeLayoutGeneratorState({ images, isMirrored: imageAreaLoop === 2 })
    }
  }, [images, imageAreaLoop])

  // Use layoutEffect to ensure DOM is updated before copying to export layer
  // This runs AFTER React has rendered the images to the visible layer
  useLayoutEffect(() => {
    if (images.length > 0) {
      // Update export layer after DOM has been updated (critical for Safari first export)
      // Without this, the first export would be empty because the visible layer
      // wouldn't contain the newly added images yet
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          copyContentToExportLayer()
        })
      })
    }
  }, [images, imageAreaLoop])

  useEffect(() => {
    if (exportResponse) {
      exportImage()
    }
  }, [exportResponse])

  useEffect(() => {
    if (motifRemoverRef.current) {
      motifRemoverClientRect.current = motifRemoverRef.current.getBoundingClientRect()
    }
  }, [motifRemoverRef])

  const exportImage = async () => {
    if (!canvasToExportRef.current || !exportLayerRef.current) {
      return
    }

    // Determine export type once at the beginning of the export process
    const isTiled = exportResponse === ExportLayoutModalResponse.TILES
    const exportSize = isTiled ? EXPORT_SIZE : SINGLE_EXPORT_SIZE
    const scaleValue = isTiled ? 1 : 3

    copyContentToExportLayer(scaleValue)

    exportLayerRef.current.style.width = `${exportSize}px`
    exportLayerRef.current.style.height = `${exportSize}px`

    // Safari fix: Make export layer visible for rendering
    exportLayerRef.current.style.visibility = 'visible'
    exportLayerRef.current.style.opacity = '1'

    // Wait for background images to load
    const imageElements = exportLayerRef.current.querySelectorAll('.image')
    const imageLoadPromises = Array.from(imageElements).map(element => {
      const bgImage = window.getComputedStyle(element as HTMLElement).backgroundImage
      const urlMatch = bgImage.match(/url\(["']?([^"']*)["']?\)/)

      if (urlMatch && urlMatch[1]) {
        return new Promise<void>(resolve => {
          const img = new Image()
          img.onload = () => resolve()
          img.onerror = () => resolve()
          img.src = urlMatch[1]
        })
      }
      return Promise.resolve()
    })

    await Promise.all(imageLoadPromises)

    // Force reflow to ensure Safari commits the render
    void exportLayerRef.current.offsetHeight

    // Wait for browser to render
    await new Promise(resolve => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 50)
        })
      })
    })

    // Safari workaround: First export primes the rendering pipeline
    // The second export consistently produces correct results
    try {
      await toJpeg(exportLayerRef.current, {
        skipAutoScale: true,
        quality: 0.92,
      })
    } catch (e) {
      console.error('First export failed (expected in Safari):', e)
    }

    // Small delay before second export
    await new Promise(resolve => setTimeout(resolve, 50))

    // Second export - this one works reliably
    const dataUrl = await toJpeg(exportLayerRef.current, {
      skipAutoScale: true,
      quality: 0.92,
    })

    // Hide export layer after capture
    if (exportLayerRef.current) {
      exportLayerRef.current.style.visibility = 'hidden'
      exportLayerRef.current.style.opacity = '0'
    }

    const img = document.createElement('img')
    img.style.position = 'absolute'
    img.style.zIndex = '2000'
    img.src = dataUrl

    // Single export: 3000x3000, Tiled export: 3x3 grid of 1000x1000 tiles = 3000x3000
    const canvasSize = SINGLE_EXPORT_SIZE
    const sourceSize = isTiled ? EXPORT_SIZE : SINGLE_EXPORT_SIZE

    img.style.width = `${sourceSize}px`
    img.style.height = `${sourceSize}px`

    canvasToExportRef.current.width = canvasSize
    canvasToExportRef.current.height = canvasSize

    const ctx = canvasToExportRef.current.getContext('2d')

    if (!ctx) return
    img.onload = () => {
      if (!ctx || !containerRef.current || !canvasToExportRef.current || !clipLayerRef.current)
        return
      const svgBoundingClientRect = clipLayerRef.current
        .querySelector('svg')
        ?.getBoundingClientRect()
      if (!svgBoundingClientRect) return

      canvasToExportRef.current.width = canvasSize
      canvasToExportRef.current.height = canvasSize

      ctx.beginPath()
      ctx.fillStyle = '#fff'
      ctx.fillRect(0, 0, canvasToExportRef.current.width, canvasToExportRef.current.height)

      if (isTiled) {
        for (let row = 0; row < 3; row++) {
          for (let col = 0; col < 3; col++) {
            ctx.drawImage(img, col * EXPORT_SIZE, row * EXPORT_SIZE, EXPORT_SIZE, EXPORT_SIZE)
          }
        }
      } else {
        ctx.drawImage(img, 0, 0, SINGLE_EXPORT_SIZE, SINGLE_EXPORT_SIZE)
      }

      handleExport(canvasToExportRef.current)
      onExportComplete?.()
    }

    setExportInvoked(false)
  }

  const handleMouseMove = (event: React.MouseEvent | React.TouchEvent): void => {
    const selectedImage = getSelectedImage()

    if (!selectedImage) return
    const pageXY = getPagePositionXY(event)

    if (rotating.current) {
      rotateImage(selectedImage, pageXY)
    }

    if (resizing.current) {
      resizeImage(selectedImage, pageXY)
    }

    if (isDragging && !rotating.current && !resizing.current) {
      moveImage(selectedImage, pageXY)

      if (
        event.type === 'touchmove' &&
        motifRemoverRef.current &&
        motifRemoverClientRect.current &&
        selectedImageRef.current
      ) {
        if (
          pageXY.x > motifRemoverClientRect.current.left &&
          pageXY.x < motifRemoverClientRect.current.right &&
          pageXY.y > motifRemoverClientRect.current.y &&
          pageXY.y < motifRemoverClientRect.current.y + motifRemoverClientRect.current.height
        ) {
          removeEnabled.current = true
          selectedImageRef.current.classList.add('to-be-removed')
          motifRemoverRef.current.classList.add('active')
        } else {
          removeEnabled.current = false
          selectedImageRef.current.classList.remove('to-be-removed')
          motifRemoverRef.current.classList.remove('active')
        }
      }
    }

    if (!isMobile) {
      lastPos.current = getPagePositionXY(event)
    } else if (rotating.current || resizing.current || isDragging) {
      lastPos.current = getPagePositionXY(event)
    }
  }

  const moveImage = (selectedImage: DraggableImage, pageXY: PositionXY) => {
    const movedImages = [...images]
    const imageToMove = movedImages.find(image => image.id === selectedImage.id)

    if (!imageToMove) return

    // Dragging item from the list to the screen
    if (!dragStartPosXY.current) {
      imageToMove.position = {
        x: pageXY.x * (1 / ratio.current) - imageToMove.size / 2,
        y: pageXY.y * (1 / ratio.current) - imageToMove.size / 2,
      }
    } else {
      // Dragging item that are already on the screen
      if (lastPos.current.x === 0) return

      imageToMove.position = {
        x: imageToMove.position.x - (lastPos.current.x - pageXY.x) * (1 / ratio.current),
        y: imageToMove.position.y - (lastPos.current.y - pageXY.y) * (1 / ratio.current),
      }
    }

    setImages(movedImages)
  }

  const rotateImage = (selectedImage: DraggableImage, pageXY: PositionXY) => {
    const rotationAmount = lastPos.current.x - pageXY.x
    const tmpImages = [...images]
    tmpImages.find(image => image.id === selectedImage.id)!.rotation =
      selectedImage.rotation + rotationAmount
    setImages(tmpImages)
  }

  const resizeImage = (selectedImage: DraggableImage, pageXY: PositionXY) => {
    let resizeAmount = -(lastPos.current.x - pageXY.x)
    const tmpImages = [...images]
    const imageToResize = tmpImages.find(image => image.id === selectedImage.id)

    if (!imageToResize) return

    const centerOfImageXY = {
      x: imageToResize.position.x + imageToResize.size / 2,
      y: imageToResize.position.y + imageToResize.size / 2,
    }

    if (!resizeDirection.current) {
      centerOfImageXY.x * ratio.current > dragStartPosXY.current!.x
        ? (resizeDirection.current = -1)
        : (resizeDirection.current = 1)
    }

    resizeAmount = resizeDirection.current * resizeAmount

    imageToResize.size =
      selectedImage.size + resizeAmount > minImageSize &&
      selectedImage.size + resizeAmount < maxImageSize
        ? selectedImage.size + resizeAmount
        : selectedImage.size
    setImages(tmpImages)
  }

  const handleMirrorSwitchChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.checked ? setImageAreaLoop(2) : setImageAreaLoop(1)

    handleMouseUp()
  }

  const copyContentToExportLayer = (scale: number = 1) => {
    if (!visibleLayerRef.current || !exportLayerRef.current || !clipLayerRef.current) return
    exportLayerRef.current.innerHTML = visibleLayerRef.current.innerHTML

    const bounding = clipLayerRef.current.querySelector('svg')!.getBoundingClientRect()

    // Apply scale to outer-image-container
    const outerContainer = exportLayerRef.current.querySelector('.outer-image-container')
    if (outerContainer) {
      ;(outerContainer as HTMLDivElement).style.width = '100%'
      ;(outerContainer as HTMLDivElement).style.height = '100%'
      ;(outerContainer as HTMLDivElement).style.transform = `scale(${scale})`
      ;(outerContainer as HTMLDivElement).style.transformOrigin = 'top left'
    }

    // Apply scale to clip-layer
    const clipLayer = exportLayerRef.current.querySelector('.clip-layer')
    if (clipLayer) {
      ;(clipLayer as HTMLDivElement).style.width = '100%'
      ;(clipLayer as HTMLDivElement).style.height = '100%'
      ;(clipLayer as HTMLDivElement).style.transform = `scale(${scale})`
      ;(clipLayer as HTMLDivElement).style.transformOrigin = 'top left'
    }

    exportLayerRef.current.querySelectorAll('.inner-image-container').forEach(image => {
      const transform = (image as HTMLDivElement).style.transform
      const x = parseFloat(transform.split('(')[1].split('px')[0])
      let xModded = (x - bounding.left * (1 / ratio.current)) * scale
      const y = parseFloat(transform.split(', ')[1].split('px')[0])
      let yModded = (y - bounding.top * (1 / ratio.current)) * scale

      // Parse rotation if exists
      const rotateMatch = transform.match(/rotate\(([^)]+)\)/)
      const rotateValue = rotateMatch ? rotateMatch[0] : 'rotate(0deg)'

      // Get original size
      const imageEl = image as HTMLDivElement
      const originalSize = parseFloat(imageEl.style.width || '0')
      const scaledSize = originalSize * scale

      imageEl.style.width = `${scaledSize}px`
      imageEl.style.height = `${scaledSize}px`
      imageEl.style.transform = `translate(${xModded}px, ${yModded}px) ${rotateValue}`
    })
  }

  const handleContainerClick = (
    event: React.MouseEvent<HTMLDivElement> | React.TouchEvent,
  ): void => {
    if (
      (event.target as HTMLDivElement).classList.contains('free-layout-container') ||
      ((event.target as HTMLDivElement).classList.contains('image-area') &&
        !isDragging &&
        !rotating.current)
    ) {
      setImages(
        images.map(image => {
          image.selected = false
          return image
        }),
      )
    }
  }

  const handleImageMouseDown = (
    event: React.MouseEvent | React.TouchEvent,
    clickedImage: DraggableImage,
  ): void => {
    event.preventDefault()
    event.stopPropagation()

    if (!images.find(image => image.id === clickedImage.id)?.selected) {
      clickedImage.selected = true
      const updatedImages = images.map(image => {
        if (clickedImage.id === image.id) return clickedImage
        image.selected = false
        return image
      })
      setImages(updatedImages)

      return
    }

    setIsDragging(true)
    dragStartPosXY.current = getPagePositionXY(event)
    lastPos.current = getPagePositionXY(event)
  }

  const handleMouseUp = (): void => {
    if (removeEnabled.current) {
      const selectedImage = images.find(img => img.selected)
      if (!selectedImageRef.current) {
        return
      }

      selectedImageRef.current.classList.add('remove')

      const selectedImageElement: HTMLDivElement | null =
        selectedImageRef.current.querySelector('.image')

      if (motifRemoverClientRect.current && selectedImageElement && selectedImage) {
        selectedImageRef.current.style.transform = `translate(${
          motifRemoverClientRect.current.left * (1 / ratio.current) +
          motifRemoverClientRect.current.width / 2 -
          selectedImage.size / 2
        }px, ${
          motifRemoverClientRect.current.y * (1 / ratio.current) +
          motifRemoverClientRect.current.height / 2 -
          selectedImage.size / 2
        }px)`
      }

      setTimeout(() => {
        setImages([...images.filter(img => !img.selected)])
        removeEnabled.current = false
      }, 400)
    }

    setSelectedMotifForFreeGeneration(undefined)
    setFreeGeneratorTouchEventPageXY(undefined)
    rotating.current = false
    resizing.current = false
    setIsDragging(false)
    dragStartPosXY.current = undefined
    resizeDirection.current = undefined
    copyContentToExportLayer()
  }

  const handleRotatorMouseDown = (event: React.MouseEvent | React.TouchEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    rotating.current = true
  }

  const handleResizerMouseDown = (event: React.MouseEvent | React.TouchEvent): void => {
    event.preventDefault()
    event.stopPropagation()
    resizing.current = true
    dragStartPosXY.current = getPagePositionXY(event)
  }

  const handleRemoverEnter = (): void => {
    if (isDragging && motifRemoverRef.current && selectedImageRef.current) {
      selectedImageRef.current.classList.add('to-be-removed')
      removeEnabled.current = true
      motifRemoverRef.current.classList.add('active')
    }
  }

  const handleRemoverLeave = (): void => {
    if (isDragging && motifRemoverRef.current && selectedImageRef.current) {
      selectedImageRef.current.classList.remove('to-be-removed')
      removeEnabled.current = false
      motifRemoverRef.current?.classList.remove('active')
    }
  }

  const getSelectedImage = (): DraggableImage => images.find(image => image.selected)!

  const { callback } = useThrottleFn(handleMouseMove, 20)

  return (
    <div
      className="free-layout-container"
      style={assetVariables}
      onMouseMove={callback}
      onTouchMove={callback}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleMouseUp}
      onMouseDown={e => handleContainerClick(e)}
      onTouchStart={e => handleContainerClick(e)}
    >
      {images.length > 0 ? (
        <CheckboxSwitch
          onSwitch={handleMirrorSwitchChange}
          isOn={imageAreaLoop === 2}
          position={{
            x: `calc(50% + 500px * ${ratio.current} + 2rem)`,
            y: `calc(50% - 500px * ${ratio.current})`,
          }}
        />
      ) : null}
      <canvas ref={canvasToExportRef} width={EXPORT_SIZE} height={EXPORT_SIZE}></canvas>
      <div
        ref={motifRemoverRef}
        className={cn('motif-remover', { show: isDragging })}
        onMouseEnter={handleRemoverEnter}
        onMouseLeave={handleRemoverLeave}
      >
        <div>{t<string>('freehandGeneratorRemove')}</div>
      </div>
      <div className="visible-layer" ref={visibleLayerRef}>
        <div
          className="clip-layer"
          style={{
            width: `${(1 / ratio.current) * 100}%`,
            height: `${(1 / ratio.current) * 100}%`,
            transform: `scale(${ratio.current})`,
          }}
          ref={clipLayerRef}
        >
          <MaskSquare />
        </div>
        <div
          style={{
            width: `${(1 / ratio.current) * 100}%`,
            height: `${(1 / ratio.current) * 100}%`,
            transform: `scale(${ratio.current})`,
          }}
          className={cn('outer-image-container', { 'hide-handlers': exportResponse })}
          ref={containerRef}
        >
          {[...Array(imageAreaLoop)].map((_n, index) => (
            <div
              className={cn('image-area', {
                'mirror-on': imageAreaLoop === 2,
                mirrored: index > 0,
              })}
              key={`image-area-${index}`}
            >
              {images.map((image, imageIndex: number) => {
                const imageUrl = image.originalSVGUrl || image.vectorizedUrl
                return imageUrl ? (
                  <div
                    ref={image.selected ? selectedImageRef : null}
                    key={`${image.id}-${imageIndex}`}
                    onMouseDown={e => handleImageMouseDown(e, image)}
                    onTouchStart={e => handleImageMouseDown(e, image)}
                    className={cn('inner-image-container', { selected: image.selected })}
                    style={{
                      width: `${image.size}px`,
                      height: `${image.size}px`,
                      transform: `translate(${image.position.x}px, ${image.position.y}px) rotate(${image.rotation}deg)`,
                    }}
                  >
                    <div
                      className="image"
                      style={{
                        backgroundImage: `url(${imageUrl})`,
                      }}
                    ></div>
                    <div
                      className="rotator"
                      onMouseDown={handleRotatorMouseDown}
                      onTouchStart={handleRotatorMouseDown}
                      style={{
                        width: `${1 * (1 + 1 - ratio.current)}px`,
                        height: `${16 * (1 + 1 - ratio.current)}px`,
                      }}
                    >
                      <div
                        className="rotator__handler"
                        style={{
                          transform: `scale(${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000})`,
                        }}
                      ></div>
                    </div>
                    <div
                      className="resizer"
                      style={{ borderWidth: `${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000}px` }}
                    >
                      <div
                        onMouseDown={handleResizerMouseDown}
                        onTouchStart={handleResizerMouseDown}
                        className="resizer__corner resizer__corner--top-left"
                        style={{
                          transform: `scale(${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000})`,
                        }}
                      ></div>
                      <div
                        onMouseDown={handleResizerMouseDown}
                        onTouchStart={handleResizerMouseDown}
                        className="resizer__corner resizer__corner--top-right"
                        style={{
                          transform: `scale(${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000})`,
                        }}
                      ></div>
                      <div
                        onMouseDown={handleResizerMouseDown}
                        onTouchStart={handleResizerMouseDown}
                        className="resizer__corner resizer__corner--bottom-right"
                        style={{
                          transform: `scale(${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000})`,
                        }}
                      ></div>
                      <div
                        onMouseDown={handleResizerMouseDown}
                        onTouchStart={handleResizerMouseDown}
                        className="resizer__corner resizer__corner--bottom-left"
                        style={{
                          transform: `scale(${1 + ((1 - ratio.current) * EXPORT_SIZE) / 1000})`,
                        }}
                      ></div>
                    </div>
                  </div>
                ) : null
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="export-layer" ref={exportLayerRef}></div>
    </div>
  )
}

export default FreeLayoutGenerator
